// Backend base URL. Unset (dev on :3000) → the local backend. Set it to '' (empty) for a
// same-origin build — the backend serves the frontend itself, so requests stay relative.
export const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:5050'

const json = (r) => r.json()
const postJson = (path, body) =>
  fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

export const getCampaigns = () => fetch(`${API_BASE}/campaigns`).then(json)
// Cheap live-sync change-token: { rev, day }. Poll this; only refetch the heavy reads when rev moves.
export const getRev = (id) => fetch(`${API_BASE}/campaigns/${id}/rev`).then(json)
export const getMap = (id) => fetch(`${API_BASE}/campaigns/${id}/map`).then(json)
export const getFactions = (id) => fetch(`${API_BASE}/campaigns/${id}/factions`).then(json)
export const getMembers = (id) => fetch(`${API_BASE}/campaigns/${id}/members`).then(json)

export const upsertUser = (user) => postJson('/users', user).then(json)

// returns { ok, status, data }
const send = async (path, body) => {
  const r = await postJson(path, body)
  const data = await r.json().catch(() => ({}))
  return { ok: r.ok, status: r.status, data }
}

export const joinCampaign = (campaignId, body) => send(`/campaigns/${campaignId}/members`, body)
// DM-only: the plaintext join codes (so a DM can read/share/reset them).
export const getAccessCodes = (id, viewer) => fetch(`${API_BASE}/campaigns/${id}/access-codes${vq(viewer)}`).then(async r => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }))
export const setAccessCodes = (id, body) => send(`/campaigns/${id}/access-codes`, body)

// Fog of war: read endpoints are projected per viewer. Pass the current user's uuid so the
// server filters to what that faction may see (DM = full truth; absent uuid = spectator).
const vq = (uuid) => (uuid ? `?actingUserUuid=${encodeURIComponent(uuid)}` : '')
export const getArmies = (id, viewer) => fetch(`${API_BASE}/campaigns/${id}/armies${vq(viewer)}`).then(json)
export const getTroopTypes = () => fetch(`${API_BASE}/troop-types`).then(json)
export const setRoster = (armyId, body) => send(`/armies/${armyId}/set-roster`, body)
export const getRecruitments = (id, viewer) => fetch(`${API_BASE}/campaigns/${id}/recruitments${vq(viewer)}`).then(json)
export const getSupplyBases = (id, viewer) => fetch(`${API_BASE}/campaigns/${id}/supply-bases${vq(viewer)}`).then(json)
export const getCaravans = (id, viewer) => fetch(`${API_BASE}/campaigns/${id}/caravans${vq(viewer)}`).then(json)
export const getCampFollowers = (id, viewer) => fetch(`${API_BASE}/campaigns/${id}/camp-followers${vq(viewer)}`).then(json)
export const getLog = (id, viewer) => fetch(`${API_BASE}/campaigns/${id}/log${vq(viewer)}`).then(json)
export const setScouting = (armyId, body) => send(`/armies/${armyId}/scouting`, body)
// Commanders (fog: players see only their own faction's). The list response also carries
// point-buy constants + per-faction substitute counts so the UI can compute budgets live.
export const getCommanders = (id, viewer) => fetch(`${API_BASE}/campaigns/${id}/commanders${vq(viewer)}`).then(json)
export const createCommander = (id, body) => send(`/campaigns/${id}/commanders`, body)
export const seedCommanders = (id, body) => send(`/campaigns/${id}/seed-commanders`, body)
export const patchCommander = (commanderId, body) =>
  fetch(`${API_BASE}/commanders/${commanderId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    .then(async r => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }))
export const deleteCommander = (commanderId, body) =>
  fetch(`${API_BASE}/commanders/${commanderId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    .then(async r => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }))
// Marching formation: whole-replace bucket assignments {assignments:{poolId:{bucket:n}}}.
export const setFormation = (armyId, body) => send(`/armies/${armyId}/formation`, body)
export const revealArmy = (id, body) => send(`/campaigns/${id}/reveal`, body)
export const getProjection = (id) => fetch(`${API_BASE}/campaigns/${id}/projection`).then(json)
export const createSupplyBase = (id, body) => send(`/campaigns/${id}/supply-bases`, body)
export const resupplyBase = (baseId, body) => send(`/supply-bases/${baseId}/resupply`, body)
export const dispatchCaravan = (baseId, body) => send(`/supply-bases/${baseId}/dispatch`, body)
// Read-only dispatch preview: route, ETA, surcharge, recommended load — nothing is committed.
export const previewDispatch = (baseId, body) => send(`/supply-bases/${baseId}/preview-dispatch`, body)
// Base settings: { target_days } tunes auto-restock; { active:false } decommissions (stock
// migrates by caravan after day 1); { active:true } reactivates.
export const patchSupplyBase = (baseId, body) =>
  fetch(`${API_BASE}/supply-bases/${baseId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    .then(async r => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }))
export const seedTreasury = (id, body) => send(`/campaigns/${id}/seed-treasury`, body)
export const patchFaction = (campaignId, fid, body) =>
  fetch(`${API_BASE}/campaigns/${campaignId}/factions/${fid}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    .then(async r => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }))
export const recruit = (id, body) => send(`/campaigns/${id}/recruit`, body)
export const createArmy = (id, body) => send(`/campaigns/${id}/armies`, body)
export const patchArmy = (armyId, body) =>
  fetch(`${API_BASE}/armies/${armyId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    .then(async r => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }))
export const addPool = (armyId, body) => send(`/armies/${armyId}/pools`, body)
export const setOrder = (armyId, body) => send(`/armies/${armyId}/order`, body)
export const planPath = (armyId, body) => send(`/armies/${armyId}/plan-path`, body)
export const raidArmy = (armyId, body) => send(`/armies/${armyId}/raid`, body)
export const provisionArmy = (armyId, body) => send(`/armies/${armyId}/provision`, body)
export const standDown = (armyId, body) => send(`/armies/${armyId}/stand-down`, body)
// Temporary detachments: { on:true } forms a fast light detachment (drops camp followers as a
// map unit); { on:false } reunites (only where followers are reachable). disperse deletes the token.
export const setDetachment = (armyId, body) => send(`/armies/${armyId}/detachment`, body)
export const disperseFollowers = (armyId, body) => send(`/armies/${armyId}/disperse-followers`, body)
export const advanceDay = (id, body) => send(`/campaigns/${id}/advance-day`, body)

// ── Phase 5: strongholds & sieges ────────────────────────────────────────────
// Strongholds are the 33 fortresses/cities seeded from the map; sieges are the live
// investments of them. Both listings carry everything the panel renders (progress,
// milestone, blockade, assault value, native garrison), computed server-side.
export const getStrongholds = (id, viewer) => fetch(`${API_BASE}/campaigns/${id}/strongholds${vq(viewer)}`).then(json)
export const getSieges = (id, viewer) => fetch(`${API_BASE}/campaigns/${id}/sieges${vq(viewer)}`).then(json)
export const seedStrongholds = (id, body) => send(`/campaigns/${id}/seed-strongholds`, body)
export const patchStronghold = (strongholdId, body) =>
  fetch(`${API_BASE}/strongholds/${strongholdId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    .then(async r => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }))

// Hired specialists on an army: engineers (10 gp/day) direct siege labor, medics (3 gp/day)
// stabilize casualties and speed the wounded back into the ranks.
export const getSpecialists = (armyId) => fetch(`${API_BASE}/armies/${armyId}/specialists`).then(json)
export const setSpecialists = (armyId, body) => send(`/armies/${armyId}/specialists`, body)

// Siege disposition: { pct: {laborers,...} } is the default whole-army split; { byPool } is the
// optional per-pool override. GET also returns the resolved head counts and the labor force.
export const getSiegeAllocation = (armyId) => fetch(`${API_BASE}/armies/${armyId}/siege-allocation`).then(json)
export const setSiegeAllocation = (armyId, body) => send(`/armies/${armyId}/siege-allocation`, body)

export const besiege = (armyId, body) => send(`/armies/${armyId}/besiege`, body)
export const liftSiege = (siegeId, body) => send(`/sieges/${siegeId}/lift`, body)
export const assaultSiege = (siegeId, body) => send(`/sieges/${siegeId}/assault`, body)
export const sallySiege = (siegeId, body) => send(`/sieges/${siegeId}/sally`, body)
export const setSiegeTerms = (siegeId, body) => send(`/sieges/${siegeId}/terms`, body)
export const resolveSiege = (siegeId, body) => send(`/sieges/${siegeId}/resolve`, body)
// GET = the modifier breakdown the DM is asked to confirm; POST = actually roll it (DM only).
export const previewSurrenderCheck = (siegeId, q = {}) => {
  const qs = new URLSearchParams(Object.entries(q).filter(([, v]) => v != null && v !== '')).toString()
  return fetch(`${API_BASE}/sieges/${siegeId}/surrender-check${qs ? `?${qs}` : ''}`).then(json)
}
export const rollSurrenderCheck = (siegeId, body) => send(`/sieges/${siegeId}/surrender-check`, body)
